/** What a severity looks like, decided once for the whole admin panel.
 *
 * Three words carry the queue: `info` wants a decision eventually, `warn`
 * blocks one circuit, `error` blocks the job or looks like abuse. They appear
 * on the Dashboard as a count per severity and in the Issues inbox as a dot
 * against every row, and the two have to agree — an admin who has learned
 * that amber means "one track is stuck" should not meet a different amber on
 * the next screen.
 *
 * The dot is small and still, not a live indicator: an issue is a thing that
 * is waiting, and a pulsing mark would claim it is happening. Colour alone
 * never carries the meaning either — the count rows name the severity beside
 * the dot, and the inbox rows are labelled for assistive technology — because
 * `warn` and `error` are two shades of the same warm hue.
 */

import type { CSSProperties } from 'react';
import { PulseDot } from '@/components/ui';
import type { Severity } from '@/lib/types';

/** Worst first: the order the Dashboard lists them and the order an admin
 *  reads them in. */
export const SEVERITY_ORDER: readonly Severity[] = ['error', 'warn', 'info'];

const TONE: Record<Severity, 'error' | 'warn' | 'neutral'> = {
  error: 'error',
  warn: 'warn',
  info: 'neutral',
};

/** A colour token, for a kicker or a number that takes the severity's hue. */
export function severityColour(severity: Severity): string {
  return severity === 'error'
    ? 'var(--color-error)'
    : severity === 'warn'
      ? 'var(--color-warn)'
      : 'var(--color-neutral-500)';
}

export interface SeverityDotProps {
  severity: Severity;
  size?: number;
  style?: CSSProperties;
}

export function SeverityDot({ severity, size = 8, style }: SeverityDotProps) {
  return <PulseDot tone={TONE[severity]} size={size} pulse={false} style={style} />;
}

export default SeverityDot;
